// redux/ugc/prayerSlice.ts

import { RootState } from "../../redux/storeTypes";
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";

import type {
  PrayerDTO,
  PrayerFeedResponse,
  CreatePrayerPayload,
  CreatePrayerResponsePayload,
} from "../../types/ugc/prayerTypes";

import { prayerApi } from "../../services/ugc/prayerApi";

// ------------------------------------------------------------
// State Shape
// ------------------------------------------------------------
interface PrayerState {
  bySlug: Record<string, PrayerDTO>;

  feed: {
    ids: string[];
    next: string | null;
    loading: boolean;
    error?: string;
  };

  explore: {
    ids: string[];
    page: number;
    next: string | null;
    hasMore: boolean;
    loading: boolean;
    error?: string;
  };

  me: {
    ids: string[];
    page: number;
    next: string | null;
    hasMore: boolean;
    loading: boolean;
    error?: string;
  };

  createStatus: "idle" | "loading" | "succeeded" | "failed";
  deleteStatus: "idle" | "loading" | "succeeded" | "failed";
  updateStatus: "idle" | "loading" | "succeeded" | "failed";
  responseStatus: "idle" | "loading" | "succeeded" | "failed";

  opError?: string;
}

// ------------------------------------------------------------
// Initial State
// ------------------------------------------------------------
const initialState: PrayerState = {
  bySlug: {},

  feed: { ids: [], next: null, loading: false },
  explore: { ids: [], page: 1, next: null, hasMore: true, loading: false },
  me: { ids: [], page: 1, next: null, hasMore: true, loading: false },

  createStatus: "idle",
  deleteStatus: "idle",
  updateStatus: "idle",
  responseStatus: "idle",
  opError: undefined,
};

// ------------------------------------------------------------
// Thunks
// ------------------------------------------------------------

export const fetchPrayerFeed = createAsyncThunk<
  PrayerFeedResponse,
  { cursor?: string } | void
>("prayers/fetchFeed", async (arg) => {
  return prayerApi.getFeed(arg?.cursor);
});

export const fetchPrayerExplore = createAsyncThunk<
  PrayerFeedResponse,
  { page?: number } | void,
  { state: RootState }
>("prayers/fetchExplore", async (arg, { getState }) => {
  const s = getState();
  const pageFromState = s.prayers?.explore?.page ?? 1;
  const page = arg?.page ?? pageFromState;
  return prayerApi.getExplore(page);
});

export const fetchMyPrayers = createAsyncThunk<
  PrayerFeedResponse,
  { page?: number } | void,
  { state: RootState }
>("prayers/fetchMine", async (arg, { getState }) => {
  const s = getState();
  const pageFromState = s.prayers?.me?.page ?? 1;
  const page = arg?.page ?? pageFromState;
  return prayerApi.getMine(page);
});

export const fetchPrayerBySlug = createAsyncThunk<PrayerDTO, string>(
  "prayers/fetchBySlug",
  async (slug) => prayerApi.getBySlug(slug)
);

export const createPrayer = createAsyncThunk<
  PrayerDTO,
  {
    payload: FormData | CreatePrayerPayload;
    onUploadProgress?: (pct: number) => void;
    signal?: AbortSignal;
  }
>("prayers/create", async ({ payload, onUploadProgress, signal }) => {
  return prayerApi.create(payload, { onUploadProgress, signal });
});

export const updatePrayerBySlug = createAsyncThunk<
  PrayerDTO,
  { slug: string; payload: FormData | Partial<CreatePrayerPayload> }
>("prayers/update", async ({ slug, payload }) => {
  return prayerApi.update(slug, payload);
});

export const deletePrayerBySlug = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: { slug: string; snapshot?: PrayerDTO } }
>("prayers/delete", async (slug, { getState, rejectWithValue }) => {
  try {
    const snapshot = getState().prayers.bySlug[slug];
    await prayerApi.remove(slug);
    return slug;
  } catch {
    const snapshot = getState().prayers.bySlug[slug];
    return rejectWithValue({ slug, snapshot });
  }
});

// ----------------------------
// Response lifecycle
// ----------------------------
export const respondToPrayer = createAsyncThunk<
  PrayerDTO,
  {
    slug: string;
    payload: FormData | CreatePrayerResponsePayload;
    onUploadProgress?: (pct: number) => void;
    signal?: AbortSignal;
  }
>("prayers/respond", async ({ slug, payload, onUploadProgress, signal }) => {
  return prayerApi.respond(slug, payload, { onUploadProgress, signal });
});

export const deletePrayerResponse = createAsyncThunk<
  PrayerDTO,
  string
>("prayers/deleteResponse", async (slug) => {
  await prayerApi.deleteResponse(slug);
  return prayerApi.getBySlug(slug);
});

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------
const addUniqueFront = (arr: string[], id: string) => {
  const i = arr.indexOf(id);
  if (i !== -1) arr.splice(i, 1);
  arr.unshift(id);
};

const addUniqueBack = (arr: string[], id: string) => {
  if (!arr.includes(id)) arr.push(id);
};

const removeFromAllLists = (state: PrayerState, slug: string) => {
  state.feed.ids = state.feed.ids.filter((x) => x !== slug);
  state.explore.ids = state.explore.ids.filter((x) => x !== slug);
  state.me.ids = state.me.ids.filter((x) => x !== slug);
};

// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------
const prayerSlice = createSlice({
  name: "prayers",
  initialState,
  reducers: {
    resetPrayerState: () => initialState,
    resetPrayerOps(state) {
      state.createStatus = "idle";
      state.deleteStatus = "idle";
      state.updateStatus = "idle";
      state.responseStatus = "idle";
      state.opError = undefined;
    },
  },

  extraReducers: (builder) => {
    // Feed
    builder
      .addCase(fetchPrayerFeed.fulfilled, (state, action) => {
        const { results, next } = action.payload;
        results.forEach((p) => {
          state.bySlug[p.slug] = p;
          addUniqueBack(state.feed.ids, p.slug);
        });
        state.feed.next = next ?? null;
      });

    // Create lifecycle
    builder
      .addCase(createPrayer.pending, (state) => {
        state.createStatus = "loading";
        state.opError = undefined;
      })
      .addCase(createPrayer.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        state.opError = undefined;

        const p = action.payload;
        state.bySlug[p.slug] = p;
        addUniqueFront(state.feed.ids, p.slug);
        addUniqueFront(state.me.ids, p.slug);
      })
      .addCase(createPrayer.rejected, (state, action) => {
        state.createStatus = "failed";
        state.opError = action.error.message || "Create prayer failed";
      });

    // Delete
    builder.addCase(deletePrayerBySlug.fulfilled, (state, action) => {
      delete state.bySlug[action.payload];
      removeFromAllLists(state, action.payload);
    });

    // Update lifecycle
    builder
      .addCase(updatePrayerBySlug.pending, (state) => {
        state.updateStatus = "loading";
        state.opError = undefined;
      })
      .addCase(updatePrayerBySlug.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        state.opError = undefined;

        const p = action.payload;
        state.bySlug[p.slug] = p;

        // Keep lists fresh if item already exists
        addUniqueBack(state.feed.ids, p.slug);
        addUniqueBack(state.explore.ids, p.slug);
        addUniqueBack(state.me.ids, p.slug);
      })
      .addCase(updatePrayerBySlug.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.opError = action.error.message || "Update prayer failed";
      });

    builder
      .addCase(respondToPrayer.pending, (state) => {
        state.responseStatus = "loading";
        state.opError = undefined;
      })
      .addCase(respondToPrayer.fulfilled, (state, action) => {
        state.responseStatus = "succeeded";
        state.opError = undefined;
        state.bySlug[action.payload.slug] = action.payload;
      })
      .addCase(respondToPrayer.rejected, (state, action) => {
        state.responseStatus = "failed";
        state.opError = action.error.message || "Save prayer response failed";
      });

    builder
      .addCase(deletePrayerResponse.pending, (state) => {
        state.responseStatus = "loading";
        state.opError = undefined;
      })
      .addCase(deletePrayerResponse.fulfilled, (state, action) => {
        state.responseStatus = "succeeded";
        state.opError = undefined;
        state.bySlug[action.payload.slug] = action.payload;
      })
      .addCase(deletePrayerResponse.rejected, (state, action) => {
        state.responseStatus = "failed";
        state.opError = action.error.message || "Delete prayer response failed";
      });


    // Me
    builder
      .addCase(fetchMyPrayers.pending, (state) => {
        state.me.loading = true; // show loader
        state.me.error = undefined;
      })
      .addCase(fetchMyPrayers.fulfilled, (state, action) => {
        state.me.loading = false;
        const { results, next } = action.payload;

        results.forEach((p) => {
          state.bySlug[p.slug] = p;
          addUniqueBack(state.me.ids, p.slug);
        });

        state.me.next = next ?? null;
        state.me.hasMore = Boolean(next);
      })
      .addCase(fetchMyPrayers.rejected, (state, action) => {
        state.me.loading = false;
        state.me.error = action.error.message || "Fetch my prayers failed";
      });

    // Explore
    builder
      .addCase(fetchPrayerExplore.pending, (state) => {
        state.explore.loading = true;
        state.explore.error = undefined;
      })
      .addCase(fetchPrayerExplore.fulfilled, (state, action) => {
        state.explore.loading = false;
        const { results, next } = action.payload;

        results.forEach((p) => {
          state.bySlug[p.slug] = p;
          addUniqueBack(state.explore.ids, p.slug);
        });

        state.explore.next = next ?? null;
        state.explore.hasMore = Boolean(next);
      })
      .addCase(fetchPrayerExplore.rejected, (state, action) => {
        state.explore.loading = false;
        state.explore.error = action.error.message || "Fetch explore prayers failed";
      });

  },
});

export const { resetPrayerState, resetPrayerOps } = prayerSlice.actions;
export default prayerSlice.reducer;